import { useCallback, useEffect, useRef } from "react";
import { trackEvent } from "services/productAnalytics";
import type { DialogCloseReason } from "services/productAnalytics/schema";

type DialogTracking = {
  // The opt-in. Without an id nothing is reported.
  dialog: string | undefined;
  open: boolean;
  hasError: boolean;
};

/**
 * Reports a dialog's lifecycle, and returns a recorder for the dismissal routes
 * the Dialog owns — call it just before handing the close on.
 *
 * The close itself is reported from the open effect's cleanup rather than from
 * the dismissal: an owner is free to ignore onClose, and just as free to close
 * the dialog without one.
 */
export function useDialogTracking({ dialog, open, hasError }: DialogTracking) {
  const via = useRef<DialogCloseReason>();

  useEffect(() => {
    if (!dialog || !open) {
      return;
    }
    via.current = undefined;
    trackEvent("dialog_opened", { dialog });
    return () => {
      // If no close route was recorded then dialog must have been closed programmatically.
      trackEvent("dialog_closed", { dialog, via: via.current ?? "programmatic" });
    };
  }, [dialog, open]);

  useEffect(() => {
    if (dialog && open && hasError) {
      trackEvent("dialog_error_shown", { dialog });
    }
  }, [dialog, open, hasError]);

  return useCallback((reason: DialogCloseReason) => {
    via.current = reason;
  }, []);
}
